import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

const STORAGE_KEY = 'aeroveraiq_simulation_state';

export interface SimulationUiState {
  enabled: boolean;
  selectedTargets: string[];
}

@Injectable({
  providedIn: 'root'
})
export class SimulationStateService {

  private readonly defaultState: SimulationUiState = {
    enabled: false,
    selectedTargets: []
  };

  private readonly stateSubject = new BehaviorSubject<SimulationUiState>(
    this.readFromStorage()
  );

  readonly state$ = this.stateSubject.asObservable();

  get state(): SimulationUiState {
    return this.stateSubject.value;
  }

  get enabled(): boolean {
    return this.stateSubject.value.enabled;
  }

  get selectedTargets(): string[] {
    return this.stateSubject.value.selectedTargets ?? [];
  }

  setEnabled(enabled: boolean): void {
    this.patch({ enabled });
  }

  setSelectedTargets(selectedTargets: string[]): void {
    this.patch({
      selectedTargets: Array.isArray(selectedTargets) ? [...selectedTargets] : []
    });
  }

  toggleEnabled(): boolean {
    const enabled = !this.enabled;
    this.setEnabled(enabled);
    return enabled;
  }

  clear(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.stateSubject.next({ ...this.defaultState, selectedTargets: [] });
  }

  private patch(partial: Partial<SimulationUiState>): void {
    const next: SimulationUiState = {
      enabled:
        partial.enabled !== undefined
          ? partial.enabled
          : this.stateSubject.value.enabled,
      selectedTargets: Array.isArray(partial.selectedTargets)
        ? [...partial.selectedTargets]
        : [...(this.stateSubject.value.selectedTargets ?? [])]
    };

    this.stateSubject.next(next);
    this.writeToStorage(next);
  }

  private readFromStorage(): SimulationUiState {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return { ...this.defaultState, selectedTargets: [] };
      }

      const parsed = JSON.parse(raw) as Partial<SimulationUiState> & {
        selectedTarget?: string;
      };

      // Backward compatible: old single selectedTarget → selectedTargets[]
      let selectedTargets: string[] = [];
      if (Array.isArray(parsed.selectedTargets)) {
        selectedTargets = parsed.selectedTargets.filter(
          (t): t is string => typeof t === 'string' && !!t.trim()
        );
      } else if (
        typeof parsed.selectedTarget === 'string' &&
        parsed.selectedTarget.trim()
      ) {
        selectedTargets = [parsed.selectedTarget.trim()];
      }

      return {
        enabled: !!parsed.enabled,
        selectedTargets
      };
    } catch {
      return { ...this.defaultState, selectedTargets: [] };
    }
  }

  private writeToStorage(state: SimulationUiState): void {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        enabled: state.enabled,
        selectedTargets: state.selectedTargets ?? []
      })
    );
  }
}
